import { createContext, Dispatch, ReactNode, useContext, useReducer } from 'react';
import {
	ColumnMapping,
	NotifyMode,
	PreviewResponse,
	ScopeMode,
	SeatsIOAction,
	TicketImport,
	UploadResponse,
	ValueMapping,
} from './types';

// Best-effort JSON parser. Returns null if parsing fails.
const parseJSON = <T,>(raw: string): T | null => {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
};

export interface WizardState {
	importId: number | null;
	upload: UploadResponse | null;

	scopeMode: ScopeMode;
	fixedEventId: number | null;

	mapping: ColumnMapping;
	valueMapping: ValueMapping;

	seatsIOAction: SeatsIOAction;
	fixExcelDates: boolean;
	dateFormat: string;
	timeFormat: string;
	assumedTimezone: string;
	seatLabelTemplate: string;
	defaultPrice: number;
	eventMapping: Record<string, number>;
	notifyMode: NotifyMode;
	sourcePlatform: string;

	preview: PreviewResponse | null;
}

const initialState: WizardState = {
	importId: null,
	upload: null,

	scopeMode: 'fixed',
	fixedEventId: null,

	mapping: {},
	valueMapping: { ticket_type: {}, ticket_type_by_event: {} },

	seatsIOAction: 'book',
	fixExcelDates: true,
	dateFormat: '1/2/06',
	timeFormat: '3:04 PM',
	assumedTimezone: 'America/New_York',
	seatLabelTemplate: '',
	defaultPrice: 0,
	eventMapping: {},
	notifyMode: 'none',
	sourcePlatform: '',

	preview: null,
};

export type WizardAction =
	| { type: 'reset' }
	| { type: 'setUpload'; payload: UploadResponse }
	| { type: 'patch'; payload: Partial<WizardState> }
	| { type: 'setPreview'; payload: PreviewResponse }
	| { type: 'hydrateFromImport'; payload: TicketImport };

function reducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'reset':
			return initialState;
		case 'setUpload':
			return {
				...state,
				importId: action.payload.import_id,
				upload: action.payload,
				mapping: action.payload.suggested_mapping,
			};
		case 'patch':
			return { ...state, ...action.payload };
		case 'setPreview':
			return { ...state, preview: action.payload };
		case 'hydrateFromImport': {
			const imp = action.payload;
			const mapping = parseJSON<ColumnMapping>(imp.column_mapping) ?? {};
			const values = parseJSON<ValueMapping>(imp.value_mapping) ?? {};
			const headers = parseJSON<string[]>(imp.raw_headers) ?? [];
			const sample = parseJSON<string[][]>(imp.sample_rows) ?? [];
			return {
				...initialState,
				importId: imp.ID,
				upload: {
					import_id: imp.ID,
					file_name: imp.file_name,
					headers,
					sample_rows: sample,
					suggested_mapping: mapping,
					unique_ticket_types: [],
					unique_event_names: [],
					row_count: imp.row_count,
				},
				scopeMode: imp.scope_mode || 'fixed',
				fixedEventId: imp.event_id ?? null,
				mapping,
				valueMapping: values,
				eventMapping: values.event ?? {},
				seatsIOAction: imp.seats_io_action || 'book',
				fixExcelDates: imp.fix_excel_dates,
				dateFormat: imp.date_format || '1/2/06',
				timeFormat: imp.time_format || '3:04 PM',
				assumedTimezone: imp.assumed_timezone || 'America/New_York',
				seatLabelTemplate: imp.seat_label_template || '',
				defaultPrice: imp.default_price || 0,
				notifyMode: imp.notify_mode || 'none',
				sourcePlatform: imp.source_platform || '',
			};
		}
		default:
			return state;
	}
}

interface ContextValue {
	state: WizardState;
	dispatch: Dispatch<WizardAction>;
}

const WizardContext = createContext<ContextValue | null>(null);

export const WizardProvider = ({ children }: { children: ReactNode }): JSX.Element => {
	const [state, dispatch] = useReducer(reducer, initialState);
	return <WizardContext.Provider value={{ state, dispatch }}>{children}</WizardContext.Provider>;
};

export const useWizard = (): ContextValue => {
	const ctx = useContext(WizardContext);
	if (!ctx) {
		throw new Error('useWizard must be used inside <WizardProvider>');
	}
	return ctx;
};
